import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export interface StepTab {
  id: string;
  label: string;
}

export type TabState = 'current' | 'completed' | 'accessible' | 'disabled';

/**
 * Presentational stepper component for the Add Order Modal.
 * Renders horizontal step tabs with scroll arrows, state indicators,
 * and emits navigation events when the user clicks an accessible tab.
 */
@Component({
  selector: 'app-order-stepper',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './order-stepper.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./order-stepper.component.scss'],
})
export class OrderStepperComponent implements AfterViewInit, OnChanges {
  @Input() tabs: StepTab[] = [];
  @Input() activeTabId = '';
  @Input() visitedTabs = new Set<string>();
  @Output() tabChange = new EventEmitter<string>();

  @ViewChild('tabListContainer') tabListContainerRef?: ElementRef<HTMLDivElement>;

  canScrollLeft = false;
  canScrollRight = false;

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.scrollActiveTabIntoView();
      this.updateArrows();
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeTabId'] || changes['tabs']) {
      setTimeout(() => {
        // After first navigation, keep the current step label visible (e.g. Payment).
        if (changes['activeTabId'] && !changes['activeTabId'].isFirstChange()) {
          this.scrollActiveTabIntoView();
        }
        this.updateArrows();
      }, 50);
    }
  }

  getTabState(tabId: string): TabState {
    if (tabId === this.activeTabId) return 'current';
    if (this.isTabCompleted(tabId)) return 'completed';
    if (this.isTabAccessible(tabId)) return 'accessible';
    return 'disabled';
  }

  onTabClick(tabId: string): void {
    if (this.isTabAccessible(tabId)) {
      this.tabChange.emit(tabId);
    }
  }

  onScroll(container: HTMLElement): void {
    const max = container.scrollWidth - container.clientWidth;
    this.canScrollLeft = container.scrollLeft > 0;
    this.canScrollRight = container.scrollLeft < max - 1;
  }

  scrollTabs(direction: 'left' | 'right', container: HTMLElement): void {
    const amount = container.clientWidth * 0.6;
    container.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  }

  private isTabCompleted(tabId: string): boolean {
    const idx = this.tabs.findIndex(t => t.id === tabId);
    const activeIdx = this.tabs.findIndex(t => t.id === this.activeTabId);
    return idx < activeIdx && this.visitedTabs.has(tabId);
  }

  private isTabAccessible(tabId: string): boolean {
    if (tabId === this.activeTabId) return true;
    if (!this.visitedTabs.has(tabId)) return false;
    const targetIdx = this.tabs.findIndex(t => t.id === tabId);
    const activeIdx = this.tabs.findIndex(t => t.id === this.activeTabId);
    return targetIdx < activeIdx && this.isTabCompleted(tabId);
  }

  private updateArrows(): void {
    const el = this.tabListContainerRef?.nativeElement;
    if (!el) return;
    const overflow = el.scrollWidth - el.clientWidth;
    this.canScrollLeft = el.scrollLeft > 0;
    this.canScrollRight = overflow > 1 && el.scrollLeft < overflow - 1;
    if (overflow > 1 && el.scrollLeft === 0) this.canScrollRight = true;
  }

  /** Scroll the horizontal tab strip so the active step (e.g. Payment) stays in view. */
  private scrollActiveTabIntoView(): void {
    const container = this.tabListContainerRef?.nativeElement;
    if (!container || !this.activeTabId) return;
    const el = container.querySelector(`[data-tab-id="${this.activeTabId}"]`) as HTMLElement | null;
    if (!el) return;

    const cRect = container.getBoundingClientRect();
    const tRect = el.getBoundingClientRect();
    const padding = 12;

    if (tRect.left < cRect.left + padding) {
      container.scrollBy({ left: tRect.left - cRect.left - padding, behavior: 'smooth' });
    } else if (tRect.right > cRect.right - padding) {
      container.scrollBy({ left: tRect.right - cRect.right + padding, behavior: 'smooth' });
    }
    // Chevron visibility after smooth scroll settles
    setTimeout(() => this.updateArrows(), 320);
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
